import { useEffect, useState } from "react";
import { Capacitor } from "@capacitor/core";
import { useNavigate } from "react-router-dom";
import { supabase } from "../supabaseClient";
import axios from "../api";

export default function ProfileManagement() {
    const nav = useNavigate();

    const [sessionUser, setSessionUser] = useState(null);
    const [busy, setBusy] = useState(false);
    const [msg, setMsg] = useState("");

    const [userMeta, setUserMeta] = useState(null);     // { user_id, auth_id, email, username }
    const [profile, setProfile] = useState(null);       // editable fields

    // form fields
    const [username, setUsername] = useState("");
    const [firstName, setFirstName] = useState("");
    const [lastName, setLastName] = useState("");
    const [phone, setPhone] = useState("");
    const [contactEmail, setContactEmail] = useState("");

    useEffect(() => {
        const init = async () => {
            const { data } = await supabase.auth.getSession();
            if (data?.session?.user) {
                setSessionUser(data.session.user);
                loadProfile();
            }
        };
        init();

        const { data: sub } = supabase.auth.onAuthStateChange((_event, s) => {
            if (s?.user) {
                setSessionUser(s.user);
                loadProfile();
            } else {
                setSessionUser(null);
                setUserMeta(null);
                setProfile(null);
            }
        });

        return () => sub?.subscription?.unsubscribe?.();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    async function loadProfile() {
        setMsg("");
        setBusy(true);
        try {
            const res = await axios.get("/profile");
            const u = res.data?.user;
            const p = res.data?.profile;

            setUserMeta(u);
            setProfile(p);

            setUsername(p?.username ?? u?.username ?? "");
            setFirstName(p?.first_name ?? "");
            setLastName(p?.last_name ?? "");
            setPhone(p?.phone ?? "");
            setContactEmail(p?.contact_email ?? u?.email ?? "");
        } catch (e) {
            console.error("Load profile error:", e?.response?.data || e);
            setMsg(e?.response?.data?.error || "Could not load profile.");
        } finally {
            setBusy(false);
        }
    }

    async function save() {
        setMsg("");
        setBusy(true);
        try {
            const payload = {
                username,
                first_name: firstName,
                last_name: lastName,
                phone,
                contact_email: contactEmail,
            };

            const res = await axios.patch("/profile", payload);
            const updated = res.data?.profile;
            if (updated) {
                setProfile(updated);
                setMsg("Saved.");
            } else {
                setMsg("Saved.");
            }
        } catch (e) {
            const code = e?.response?.data?.code;
            const err = e?.response?.data?.error;

            if (code === "USERNAME_TAKEN") setMsg("That username is already taken.");
            else if (code === "BAD_USERNAME") setMsg(err || "Username format is invalid.");
            else if (code === "BAD_EMAIL") setMsg("That email doesn’t look valid.");
            else setMsg(err || "Could not save changes.");

            console.error("Save profile error:", e?.response?.data || e);
        } finally {
            setBusy(false);
        }
    }

    const passesBasePath = Capacitor.isNativePlatform() ? "/my-pass" : "/passes";

    if (!sessionUser) {
        return (
            <div className="settings-shell">
                <div className="settings-hero">
                    <h1 className="settings-h1">Profile Management</h1>
                    <p className="settings-sub">Log in to edit your account details.</p>
                </div>

                <div className="settings-card">
                    <div className="settings-list">
                        <button className="auth-primary" onClick={() => nav("/login")}>
                            Login to Continue
                        </button>
                        <button className="app-btn secondary" onClick={() => nav("/register")}>
                            Create Account
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="settings-shell">
            <div className="settings-hero" style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <button className="app-btn secondary" onClick={() => nav(-1)}>← Back</button>
                <div>
                    <h1 className="settings-h1" style={{ margin: 0 }}>Profile Management</h1>
                    <p className="settings-sub" style={{ margin: "6px 0 0" }}>
                        Update your account details.
                    </p>
                </div>
            </div>

            <div className="settings-card">
                <div className="settings-title">Pass Shortcuts</div>
                <div className="settings-list">
                    <button className="settings-item btnlike" type="button" onClick={() => nav(`${passesBasePath}#claim`)}>
                        <div className="settings-item-left settings-item-left-row">
                            <div className="settings-item-icon">🎟️</div>
                            <div>
                                <div className="settings-item-title">Redeem Pass</div>
                                <div className="settings-item-sub">Claim a pass that was emailed to you</div>
                            </div>
                        </div>
                        <div className="settings-item-right"><span className="settings-chevron">›</span></div>
                    </button>
                    <button className="settings-item btnlike" type="button" onClick={() => nav(`${passesBasePath}#child-passes`)}>
                        <div className="settings-item-left settings-item-left-row">
                            <div className="settings-item-icon">🧒</div>
                            <div>
                                <div className="settings-item-title">Child Passes</div>
                                <div className="settings-item-sub">View and assign the child passes attached to your account</div>
                            </div>
                        </div>
                        <div className="settings-item-right"><span className="settings-chevron">›</span></div>
                    </button>
                </div>
            </div>

            <div className="settings-card">
                <div className="settings-title">Account Details</div>

                <div className="auth-card" style={{ marginTop: 12 }}>
                    <div className="auth-form">
                        <label className="auth-label">
                            Username
                            <input
                                className="auth-input"
                                value={username}
                                onChange={(e) => setUsername(e.target.value)}
                                placeholder="e.g. sabastian_b"
                                disabled={busy}
                            />
                        </label>

                        <label className="auth-label">
                            Personal Info (First Name)
                            <input
                                className="auth-input"
                                value={firstName}
                                onChange={(e) => setFirstName(e.target.value)}
                                placeholder="First name"
                                disabled={busy}
                            />
                        </label>

                        <label className="auth-label">
                            Personal Info (Last Name)
                            <input
                                className="auth-input"
                                value={lastName}
                                onChange={(e) => setLastName(e.target.value)}
                                placeholder="Last name"
                                disabled={busy}
                            />
                        </label>

                        <label className="auth-label">
                            Phone
                            <input
                                className="auth-input"
                                value={phone}
                                onChange={(e) => setPhone(e.target.value)}
                                placeholder="(555) 555-5555"
                                disabled={busy}
                            />
                        </label>

                        <label className="auth-label">
                            Email (Contact)
                            <input
                                className="auth-input"
                                value={contactEmail}
                                onChange={(e) => setContactEmail(e.target.value)}
                                placeholder="name@email.com"
                                disabled={busy}
                            />
                        </label>

                        <button className="auth-primary" onClick={save} disabled={busy}>
                            {busy ? "Saving…" : "Save Changes"}
                        </button>

                        {msg ? <div className="auth-msg">{msg}</div> : null}
                    </div>
                </div>

                {/* IDs (read-only) */}
                <div className="settings-list" style={{ marginTop: 12 }}>
                    <div className="settings-item">
                        <div className="settings-item-left">
                            <div className="settings-item-title">Account User ID</div>
                            <div className="settings-item-sub">Internal identifier (read-only)</div>
                        </div>
                        <div className="settings-item-right mono">
                            {userMeta?.auth_id ? String(userMeta.auth_id).slice(0, 8) + "…" : "—"}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
